import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { HelpCircle, ClipboardList, Users, Rocket, ArrowRight, ArrowDown } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Persistent-session toggle. Defaults to ``true`` because 99% of
  // Rim Task users are shop-floor operators on shared factory
  // tablets who want to stay signed in for weeks; the checkbox is
  // there for the rare public-computer / kiosk case. Selection is
  // remembered across visits via localStorage so an operator who
  // once turned it off doesn't get re-prompted every login.
  const [rememberMe, setRememberMe] = useState(() => {
    try { return localStorage.getItem("rim.remember_me") !== "0"; }
    catch { return true; }
  });
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Honor ?next= so bridges like /chat-support can round-trip through login.
  const next = new URLSearchParams(location.search).get("next");

  // Already signed in (e.g. browser Back landed here) — never show the
  // form; bounce straight back into the app with replace so Back can't
  // park on /login.
  if (!loading && user && typeof user === "object") {
    return <Navigate to={next && next.startsWith("/") ? next : "/dashboard"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { localStorage.setItem("rim.remember_me", rememberMe ? "1" : "0"); }
    catch { /* Safari private mode — ignore */ }
    const res = await login(email, password, rememberMe);
    setBusy(false);
    if (res.ok) {
      toast.success(rememberMe ? "Welcome back — stay signed in for 365 days." : "Welcome back.");
      // replace: /login must not stay in the history stack, otherwise the
      // browser Back button would strand the user on the login form.
      navigate(next && next.startsWith("/") ? next : "/dashboard", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F6]">
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0D0D0D] text-white relative overflow-hidden">
        <Link to="/"><Logo size={64} textSize="text-xl" className="text-white" /></Link>
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500 mb-6">/ welcome back</div>
          <h2 className="font-heading font-black tracking-tighter text-5xl xl:text-6xl leading-[0.95]">
            Pick up<br/>
            <span className="text-[#FF3B30]">where you left off.</span>
          </h2>
          <p className="mt-6 text-sm text-neutral-400 max-w-md leading-relaxed">
            Your team is waiting. Get back to shipping the work that matters.
          </p>
          <button
            type="button"
            onClick={() => document.getElementById("login-how-it-works")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="login-how-link"
            className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-400 hover:text-white transition-colors">
            <ArrowDown className="h-3.5 w-3.5" /> First time? See how Rim Task works
          </button>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-600">
          v 1.0 · est. 2026
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md slide-up">
          <Link to="/" className="lg:hidden block mb-10"><Logo size={64} textSize="text-xl" /></Link>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C5C5C] mb-4">001 · Sign in</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl mb-10">Welcome.</h1>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-bold">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none border-[#0D0D0D] h-12 text-base"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-bold">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none border-[#0D0D0D] h-12 text-base"
                data-testid="login-password-input"
              />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                data-testid="login-remember-me"
                className="h-4 w-4 accent-[#FF3B30] cursor-pointer"
              />
              <Label
                htmlFor="remember-me"
                className="text-xs font-mono uppercase tracking-widest text-[#5C5C5C] cursor-pointer select-none"
              >
                Remember me on this device (365 days)
              </Label>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-[#FF3B30] border border-[#FF3B30] p-3 font-medium">{error}</div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full rounded-none bg-[#FF3B30] hover:bg-[#0D0D0D] text-white h-12 text-sm font-bold tracking-wide"
            >
              {busy ? "Signing in…" : "Sign in →"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-[#5C5C5C]">
            New to Rim Task?{" "}
            <Link to="/register" data-testid="login-register-link" className="font-bold text-[#0D0D0D] underline underline-offset-4">Create an account</Link>
          </p>

          <button
            type="button"
            onClick={() => document.getElementById("login-how-it-works")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="login-help-btn"
            className="mt-6 lg:hidden inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#5C5C5C] hover:text-[#0D0D0D] transition-colors">
            <HelpCircle className="h-3.5 w-3.5" /> How Rim Task works
          </button>

          <div className="mt-12 border-t border-[#E2E2E2] pt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C]">
            Demo: admin@rimtask.app / Admin123!
          </div>
        </div>
      </div>
    </div>

    {/* How it Works — team-onboarding strip. Anchored to #login-how-it-works
        so the "First time?" link on the brand panel and the mobile "How Rim
        Task works" button can scroll to it. Uses the same signal-red + black
        brand palette as the public shop's guide for visual consistency. */}
    <section
      id="login-how-it-works"
      data-testid="login-how-it-works"
      aria-labelledby="login-how-heading"
      className="bg-white border-t-2 border-[#0D0D0D]">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="text-center mb-10 md:mb-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">/ how rim task works</div>
          <h2 id="login-how-heading" className="text-3xl md:text-5xl font-black tracking-tight text-[#0D0D0D]">
            Delegate. Collaborate. <span className="text-[#FF3B30]">Deliver.</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-neutral-600 max-w-xl mx-auto">
            One workspace for your whole team — tasks, orders, chats, files and
            factory logs all in one place. Here's how it flows.
          </p>
        </div>

        <ol className="relative grid gap-8 md:gap-6 md:grid-cols-3">
          <div
            aria-hidden="true"
            className="hidden md:block absolute top-14 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-[#0D0D0D]/20 to-transparent"
          />

          {[
            {
              n: 1,
              icon: ClipboardList,
              title: "Delegate",
              body: "Create tasks and orders. Assign teammates with priority, due dates and attachments — everyone knows what's next.",
              accent: "red",
            },
            {
              n: 2,
              icon: Users,
              title: "Collaborate",
              body: "Real-time chat with voice notes, threaded discussions on every order, and instant WhatsApp briefings for the field team.",
              accent: "black",
            },
            {
              n: 3,
              icon: Rocket,
              title: "Deliver",
              body: "Track progress on live dashboards. Close tasks, dispatch orders, and keep customers updated — all from one screen.",
              accent: "red",
            },
          ].map((step) => {
            const Icon = step.icon;
            const isRed = step.accent === "red";
            return (
              <li
                key={step.n}
                data-testid={`login-how-step-${step.n}`}
                className="relative flex flex-col items-center text-center px-4 md:px-6">
                <div className="relative mb-5">
                  <div
                    className={`w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center shadow-lg transition-transform duration-500 hover:scale-105 hover:rotate-3 ${
                      isRed ? "bg-[#FF3B30] text-white" : "bg-[#0D0D0D] text-white"
                    }`}>
                    <Icon className="h-11 w-11 md:h-12 md:w-12" strokeWidth={1.75} />
                  </div>
                  <span
                    className={`absolute -top-2 -right-2 w-9 h-9 rounded-full flex items-center justify-center font-mono font-black text-sm shadow-md ring-4 ring-white ${
                      isRed ? "bg-[#0D0D0D] text-white" : "bg-[#FF3B30] text-white"
                    }`}
                    aria-hidden="true">
                    {step.n}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-black tracking-tight text-[#0D0D0D] mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed max-w-[26ch]">
                  {step.body}
                </p>
              </li>
            );
          })}
        </ol>

        <div className="mt-12 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="login-how-back-to-form"
            className="inline-flex items-center gap-2 bg-[#FF3B30] text-white hover:bg-[#0D0D0D] px-6 h-11 text-xs font-bold uppercase tracking-widest transition-colors">
            Sign in <ArrowRight className="h-4 w-4" />
          </button>
          <Link
            to="/register"
            data-testid="login-how-register-link"
            className="inline-flex items-center gap-2 border border-[#0D0D0D] text-[#0D0D0D] hover:bg-[#0D0D0D] hover:text-white px-6 h-11 text-xs font-bold uppercase tracking-widest transition-colors">
            Create an account
          </Link>
        </div>
      </div>
    </section>
    </div>
  );
}
